/**
 * Story Generation Service
 * 
 * Generates contextual story snippets for exploration mode via Firebase Functions
 * Secure server-side proxy protects the OpenAI API key
 */

import { AxialCoord, TerrainType, TERRAIN_NAMES, FactionId } from '../types/terrain';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase';

// Initialize Firebase Functions
const functions = getFunctions(app);

export interface StorySnippet {
  text: string;
  hasMonster: boolean;
  monsterName?: string;
  audioUrl?: string;
  isUnexploredHex?: boolean; // Flag for whether current hex is unexplored globally
}

export interface StoryContext {
  currentHex: AxialCoord;
  terrain: TerrainType;
  faction: FactionId;
  previousHex?: AxialCoord;
  previousTerrain?: TerrainType;
  playerName?: string;
  requestMonster?: boolean;  // Client requests a monster encounter in the story
  monsterName?: string;  // Specific monster to include in the story
}

/**
 * Generate a story snippet for hex exploration via Firebase Function
 */
export async function generateStorySnippet(context: StoryContext): Promise<StorySnippet> {
  console.log('🤖 Generating AI story snippet via Firebase Function...', {
    hex: `(${context.currentHex.q}, ${context.currentHex.r})`,
    terrain: TERRAIN_NAMES[context.terrain],
    faction: context.faction || 'wilderness',
    hasPreviousHex: !!context.previousHex,
  });

  try {
    // Use Firebase Functions callable (recommended) or direct HTTP call
    const generateStory = httpsCallable(functions, 'generateStorySnippet');
    
    console.log('📤 Sending request to Firebase Function...');
    const startTime = Date.now();
    
    const result = await generateStory({
      currentHex: context.currentHex,
      terrain: context.terrain,
      faction: context.faction || null,
      previousHex: context.previousHex || null,
      previousTerrain: context.previousTerrain,
      playerName: context.playerName || null,
      requestMonster: context.requestMonster || false,
      monsterName: context.monsterName || null,
    });

    const duration = Date.now() - startTime;
    const data = result.data as { text: string; hasMonster: boolean; monsterName?: string; audioUrl?: string };
    
    if (!data.text) {
      console.warn('⚠️ Empty response from function, using fallback');
      return getFallbackStory(context);
    }

    console.log('✅ AI story generated successfully!', {
      duration: `${duration}ms`,
      length: `${data.text.length} characters`,
      preview: data.text.substring(0, 100) + '...',
      source: 'Firebase Function (secure)',
      hasAudio: !!data.audioUrl,
      monsterName: data.monsterName || 'none',
    });

    return {
      text: data.text,
      hasMonster: data.hasMonster,
      monsterName: data.monsterName, // Preserve the monster name from the response
      audioUrl: data.audioUrl,
    };
  } catch (error) {
    console.error('❌ Error calling Firebase Function:', error);
    if (error instanceof Error) {
      console.error('   Error message:', error.message);
    }
    return getFallbackStory(context);
  }
}

// Note: Prompt building is now handled server-side in Firebase Function

/**
 * Fallback story if API fails
 */
function getFallbackStory(context: StoryContext): StorySnippet {
  const terrainName = TERRAIN_NAMES[context.terrain];
  const hasMonster = context.requestMonster || (context.terrain !== TerrainType.Settlement && Math.random() < 0.2);

  const fallbackText = `You step into the ${terrainName.toLowerCase()}. The landscape stretches before you, filled with the quiet mysteries of this ancient land.`;

  console.log('📝 Using fallback story (not AI-generated)');

  return {
    text: fallbackText,
    hasMonster,
    monsterName: context.monsterName || undefined, // Preserve monster name if provided
  };
}

